import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { User } from '../types/user'
import { formatUserName } from '../utils/name'
import { Avatar } from './Avatar'
import './MentionAutocomplete.css'

interface MentionAutocompleteProps {
  query: string
  users: User[]
  isLoading?: boolean
  onSelect: (user: User) => void
  onClose: () => void
  position: { top: number; left: number }
  /** Ref to attach to the dropdown container (used for click-outside when rendered via portal) */
  containerRef?: React.RefObject<HTMLDivElement>
}

export function MentionAutocomplete({
  query,
  users,
  isLoading = false,
  onSelect,
  onClose,
  position,
  containerRef: containerRefProp,
}: MentionAutocompleteProps) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const internalRef = useRef<HTMLDivElement>(null)
  const containerRef = containerRefProp ?? internalRef

  // Filter users based on query
  const filteredUsers = users.filter((user) => {
    const searchTerm = query.toLowerCase()
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase()
    const email = user.email?.toLowerCase() || ''
    const username = user.username?.toLowerCase() || ''
    return (
      fullName.includes(searchTerm) ||
      email.includes(searchTerm) ||
      username.includes(searchTerm)
    )
  })

  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((prev) =>
          prev < filteredUsers.length - 1 ? prev + 1 : prev
        )
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (filteredUsers[selectedIndex]) {
          onSelect(filteredUsers[selectedIndex])
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [filteredUsers, selectedIndex, onSelect, onClose])

  const dropdown = (
    <div
      ref={containerRef as React.RefObject<HTMLDivElement>}
      className="mention-autocomplete"
      style={{ top: `${position.top}px`, left: `${position.left}px` }}
    >
      {isLoading && filteredUsers.length === 0 ? (
        <div className="mention-autocomplete-loading">Loading...</div>
      ) : filteredUsers.length === 0 ? (
        <div className="mention-autocomplete-loading">No team members found</div>
      ) : (
        filteredUsers.map((user, index) => (
        <div
          key={user.id}
          className={`mention-autocomplete-item ${index === selectedIndex ? 'selected' : ''}`}
          onClick={() => onSelect(user)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <Avatar user={user} size="small" />
          <div className="mention-autocomplete-item-info">
            <div className="mention-autocomplete-item-name">
              {formatUserName(user)}
            </div>
            {user.email && (
              <div className="mention-autocomplete-item-email">{user.email}</div>
            )}
          </div>
        </div>
      ))
      )}
    </div>
  )

  return createPortal(dropdown, document.body)
}

